"use client";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ResponsiveContainer
} from "@cmp-src/primitives/chart";
import { ResGetUser } from "@gettactic/api";
import { columnXSpacing } from "@gettactic/components/src/contexts/ColumnWidthContext";
import { limitWeekDateStrings } from "@gettactic/helpers";
import { useMemo } from "react";
import { NoDataMessage } from "./ChartNoDataMessage";

// Geometry injected by recharts when rendering a custom bar shape
interface BarShapeProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  index?: number;
}

export const WeeklyChart = ({
  chartInfo: { chartData, chartConfig },
  myId,
  userCount,
  daysCount
}: {
  isLoading: boolean;
  chartInfo: {
    chartData: {
      name: string;
      inOffice: number;
      favorite: number;
    }[];
    chartConfig: ChartConfig;
  };
  myId: ResGetUser["id"];
  userCount: number;
  daysCount: number;
}) => {
  const noOneInTheOffice = useMemo(() => userCount < 1, [userCount]);

  // Drop weekend days (not a positional slice) so a 5-day chart keeps the
  // weekdays regardless of the configured week start day
  const limitedChartData = useMemo(() => {
    const dates = limitWeekDateStrings(
      chartData.map((dataPoint) => dataPoint.name),
      daysCount
    );
    return chartData.filter((dataPoint) => dates.includes(dataPoint.name));
  }, [chartData, daysCount]);

  // Custom shape renderer for inOffice bars - checks if favorite exists for this day
  const InOfficeShape = (props: BarShapeProps) => {
    const { x = 0, y = 0, width = 0, height = 0, index = -1 } = props;
    const dataPoint = limitedChartData[index];
    const hasFavorite = dataPoint && dataPoint.favorite > 0;

    // Use rounded corners only when favorite doesn't exist for this data point
    const radius = hasFavorite ? [0, 0, 10, 10] : [10, 10, 10, 10];
    const [topLeft, topRight, bottomRight, bottomLeft] = radius;

    if (height === 0) return null;

    // Ensure the shape uses the full container width by not subtracting from edges
    return (
      <path
        d={`
          M ${x},${y + topLeft}
          Q ${x},${y} ${x + topLeft},${y}
          L ${x + width - topRight},${y}
          Q ${x + width},${y} ${x + width},${y + topRight}
          L ${x + width},${y + height - bottomRight}
          Q ${x + width},${y + height} ${x + width - bottomRight},${y + height}
          L ${x + bottomLeft},${y + height}
          Q ${x},${y + height} ${x},${y + height - bottomLeft}
          Z
        `}
        fill="var(--color-inOffice)"
      />
    );
  };

  // Custom shape renderer for favorite bars - checks if inOffice exists for this day
  const FavoriteShape = (props: BarShapeProps) => {
    const { x = 0, y = 0, width = 0, height = 0, index = -1 } = props;
    const dataPoint = limitedChartData[index];
    const hasInOffice = dataPoint && dataPoint.inOffice > 0;

    // Use rounded corners only when inOffice doesn't exist for this data point
    const radius = hasInOffice ? [10, 10, 0, 0] : [10, 10, 10, 10];
    const [topLeft, topRight, bottomRight, bottomLeft] = radius;

    if (height === 0) return null;

    // Ensure the shape uses the full container width by not subtracting from edges
    return (
      <path
        d={`
          M ${x},${y + topLeft}
          Q ${x},${y} ${x + topLeft},${y}
          L ${x + width - topRight},${y}
          Q ${x + width},${y} ${x + width},${y + topRight}
          L ${x + width},${y + height - bottomRight}
          Q ${x + width},${y + height} ${x + width - bottomRight},${y + height}
          L ${x + bottomLeft},${y + height}
          Q ${x},${y + height} ${x},${y + height - bottomLeft}
          Z
        `}
        fill="var(--color-favorite)"
      />
    );
  };

  return (
    <div className="w-full" style={{ padding: 0, margin: 0 }}>
      <ResponsiveContainer width="100%" height={100}>
        {userCount === 0 && noOneInTheOffice ? (
          <NoDataMessage myId={myId} />
        ) : (
          <ChartContainer config={chartConfig} className="w-full">
            <BarChart
              accessibilityLayer
              data={limitedChartData}
              barCategoryGap={columnXSpacing}
              barGap={0}
              margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
            >
              <CartesianGrid vertical={false} />
              <ChartTooltip
                content={
                  <ChartTooltipContent className="text-white" hideLabel />
                }
              />
              <Bar dataKey="inOffice" stackId="a" shape={<InOfficeShape />} />
              <Bar dataKey="favorite" stackId="a" shape={<FavoriteShape />} />
            </BarChart>
          </ChartContainer>
        )}
      </ResponsiveContainer>
    </div>
  );
};
